import { ConnectionStatus, DataFlow } from '../../catalog/enums';
import { ProductApp, type IntegrationConnection } from './types';

/**
 * The subset of fields `toIntegrationConnection` reads off the base integration
 * (a synthesized-list entry, or `{}` for the reporting-webhook case). Ported
 * from the Ember overview controller's `toIntegrationConnection` destructure.
 */
export interface ConnectionInput {
  id?: string;
  name?: string;
  icon?: string;
  imageSrc?: string;
  path?: string;
  dynamicSegment?: string;
  models?: (string | number)[];
  dataFlow?: DataFlow | '' | null;
  category?: string;
  createdAt?: number;
  lastActivity?: number;
  status?: ConnectionStatus;
}

const RECENT_DATA_WINDOW_SECONDS = 60 * 60 * 24;

/** Whether `lastActivity` (unix seconds) falls inside the last 24 hours. */
export function hasRecentData(lastActivity?: number): boolean {
  if (lastActivity == null || lastActivity === 0) {
    return false;
  }
  const oneDayAgo = Date.now() / 1000 - RECENT_DATA_WINDOW_SECONDS;
  return lastActivity > oneDayAgo;
}

/**
 * Merge a base integration and a parser override into a Journeys
 * `IntegrationConnection`. A connected, non-channel connection with no recent
 * data is demoted to `noRecentData` with the fixed status message (the 24h
 * check ported from the Ember controller).
 */
export function toIntegrationConnection(
  i: ConnectionInput,
  override: Partial<IntegrationConnection> = {},
): IntegrationConnection {
  const baseDataFlow = i.dataFlow === '' ? null : (i.dataFlow ?? null);
  const merged: IntegrationConnection = {
    id: i.id ?? '',
    // Type here is not the integration type, but the integration id.
    type: i.id ?? '',
    name: i.name ?? '',
    icon: i.icon,
    imageSrc: i.imageSrc,
    path: i.path ?? '',
    dynamicSegment: i.dynamicSegment,
    models: i.models,
    dataFlow: baseDataFlow,
    category: i.category,
    product: ProductApp.journeys,
    connectedTo: [],
    createdAt: i.createdAt,
    lastActivity: i.lastActivity,
    status: i.status,
    ...override,
  };

  if (
    merged.dataFlow !== DataFlow.channel &&
    merged.status === ConnectionStatus.connected &&
    !hasRecentData(merged.lastActivity)
  ) {
    merged.status = ConnectionStatus.noRecentData;
    merged.statusMessage = 'No data has been processed in the last 24 hours.';
  }

  return merged;
}
